import { Box, Button, Card, Link, Skeleton, Stack, Tab, Typography } from "@mui/material";
import { FC, useState, useEffect } from "react";

import { Link as RouterLink } from "react-router";
import OrderHorizontalCard from "../../Cards/OrderHorizontalCard";

import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { routePaths } from "../../../Router/router";
import OrderHorizontalAdvancedCard from "../../Cards/OrderHorizontalAdvancedCard";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import PanelTabs from "../PanelTabs";

import TvIcon from "@mui/icons-material/Tv";
import { orderService, thumbnailService } from "../../../Core/Api/services";
import { GetOrderResponse } from "../../../Core/Api/Types/get-order-response";
import { GetCurrencyChar, GetOrderProductReadableType, GetPriorityFromInteger } from "../../../Utils/helpers";
import ResourceNotification from "../../Notification/resource-notification";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import OrderItemPanel from "../OrderItemPanel/order-item-panel";
import { useTranslation } from "react-i18next";

export interface OrderPanelProps {
  width?: number;
  open: boolean;
  orderId: number;
  onClose?: React.MouseEventHandler;
  onClickConfigDetails?: (itemId: number) => void;
  advanced?: boolean;
  missingAssets?: number;
}

const OrderPanel: FC<OrderPanelProps> = (props) => {
  const { t } = useTranslation();
  const { orderId, advanced = false, missingAssets = 0, onClickConfigDetails } = props;

  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState<GetOrderResponse>();

  const [openPanelOrder, setOpenPanelOrder] = useState(false);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState<number>(-1);

  const hidePanelOrderItem = () => {
    setOpenPanelOrder(false);
  };

  const handleItemClick = (itemId: number) => {
    setSelectedOrderItemId(itemId);
    setOpenPanelOrder(true);

    onClickConfigDetails && onClickConfigDetails(itemId);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    setLoading(true);

    if (orderId > 0) {
      orderService.GetOrderById(orderId).then(function (response) {
        setOrderData(response);
        setLoading(false);
      });
    }
  }, [orderId]);

  return (
    <>
      <Panel
        header={
          <PanelHeader
            onClose={props.onClose as PanelHeaderProps["onClose"]}
            rightContent={
              <Link
                component={RouterLink}
                to={`${routePaths.ordersOverview.path}/${orderId}`}
                underline="hover"
                variant="subtitle2"
                color="primary.text"
              >
                <OpenInFullOutlinedIcon sx={{ mr: 0.5 }} />
                {t("common:open")}
                {/* View more details <ArrowForwardRoundedIcon sx={{ ml: 1 }} /> */}
              </Link>
            }
          />
        }
        open={props.open}
        onClose={props.onClose}
        reload={orderId}
      >
        {isLoading ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={130} />
            <Stack direction="row" spacing={0.5}>
              <Skeleton variant="rounded" height={40} width={146} />
              <Skeleton variant="rounded" height={40} width={146} />
              <Skeleton variant="rounded" height={40} width={146} />
            </Stack>
            <Skeleton variant="rounded" height={15} />
            <Skeleton variant="rounded" height={30} />
            <Skeleton variant="rounded" height={15} width={220} />
            <Stack spacing={10}>
              <Box></Box>
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={15} />
                <Skeleton variant="rounded" height={15} width={292} />
                <Skeleton variant="rounded" height={15} width={146} />
              </Stack>
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={15} />
                <Skeleton variant="rounded" height={15} width={292} />
                <Skeleton variant="rounded" height={15} width={146} />
              </Stack>
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={15} />
                <Skeleton variant="rounded" height={15} width={292} />
                <Skeleton variant="rounded" height={15} width={146} />
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <>
            <Box sx={{ pb: 2 }}>
              <Typography variant="h6" color="text.tier">
                {t("panel:body.orderId", { id: orderId })}
              </Typography>
              <Typography variant="h2" component="h2">
                {orderData?.order?.reference}
              </Typography>
            </Box>
            {missingAssets > 0 && missingAssets <= 2 && (
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1}>
                      <TvIcon color="info" fontSize="small" sx={{ mt: 0.25 }} />
                      <Stack>
                        <Typography>{t("common:selfBroadcastedFileToOcs")}</Typography>
                        <Typography variant="body2" color="text.tier">
                          {t("panel:body.missingAssets", { count: 3 })}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Button variant="icon" size="small">
                      <BuildCircleOutlinedIcon fontSize="small" />
                    </Button>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1}>
                      <TvIcon color="info" fontSize="small" sx={{ mt: 0.25 }} />
                      <Stack>
                        <Typography>{t("panel:orderPanel.body.youtubeAntiPiracy")}</Typography>
                        <Typography variant="body2" color="text.tier">
                          {t("panel:body.missingAssets", { count: 2 })}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Button variant="icon" size="small">
                      <BuildCircleOutlinedIcon fontSize="small" />
                    </Button>
                  </Stack>
                  <Button
                    size="small"
                    endIcon={<BuildCircleOutlinedIcon fontSize="small" />}
                    component={RouterLink}
                    to={routePaths.ingestNewRequest.path}
                    fullWidth
                  >
                    {t("button:requestAllMissingAssets")}
                  </Button>
                </Stack>
              </Card>
            )}
            {missingAssets > 2 && (
              <Card variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TvIcon color="info" fontSize="small" sx={{ mt: 0.25 }} />
                    <Typography>{t("panel:body.missingAssets", { count: missingAssets })}</Typography>
                  </Stack>
                  <Button
                    variant="icon"
                    size="small"
                    startIcon={<BuildCircleOutlinedIcon fontSize="small" />}
                    component={RouterLink}
                    to={routePaths.ingestNewRequest.path}
                  >
                    {t("common:request")}
                  </Button>
                </Stack>
              </Card>
            )}

            <PanelTabs value={activeTab} onChange={handleTabChange} aria-label="basic tabs example" variant="fullWidth">
              <Tab label={t("common:details")} {...a11yProps(0)} />
              <Tab label={t("common:activities")} {...a11yProps(1)} />
            </PanelTabs>
            <Box pt={3} pb={3}>
              <PanelTabContent index={0} activePanelIndex={activeTab} id="order-tabpanel">
                <Stack spacing={1}>
                  {orderData?.order?.items.map((item) =>
                    advanced ? (
                      <OrderHorizontalAdvancedCard
                        title={item.titleName!}
                        titleId={item.relatedTitleId!}
                        itemId={item.id}
                        subtitle=""
                        thumbnail={thumbnailService.GetObjectPosterUrl(item.relatedTitleId, ThumbnailSize.small)}
                        priority={GetPriorityFromInteger(item.priority!)}
                        config={{
                          title: t("common:selfBroadcastedFileToOcs"),
                          configType: item.type,
                          price:
                            item.price === null
                              ? ""
                              : item.price?.price!.toString() + " " + GetCurrencyChar(item.price?.currency!),
                          content: { setting: item.summarizedInfos! },
                          //attachement: "Attachement.jpg",
                        }}
                        onClickConfigDetails={() => handleItemClick(item.id)}
                      />
                    ) : (
                      <OrderHorizontalCard
                        key={item.id}
                        config={{
                          title: GetOrderProductReadableType(item.type!),
                          configType: item.type!,
                          price:
                            item.price === null
                              ? ""
                              : item.price?.price!.toString() + " " + GetCurrencyChar(item.price?.currency!),
                          content: { setting: item.summarizedInfos! },
                        }}
                        title={item.titleName!}
                        titleId={item.relatedTitleId!}
                        subtitle=""
                        thumbnail={thumbnailService.GetObjectPosterUrl(item.relatedTitleId, ThumbnailSize.small)}
                        status={item.status!}
                        onClickConfigDetails={() => handleItemClick(item.id)}
                      />
                    )
                  )}
                </Stack>
              </PanelTabContent>
              <PanelTabContent index={1} activePanelIndex={activeTab} id="order-tabpanel">
                <Stack spacing={1}>
                  <ResourceNotification resourceId={orderData?.order?.id} resourceKind={ActivityKind.Order} />
                  {/* TODO : test ui and see if Divider is needed like below
             {NotificationData.notifications.map((data) => (
              <React.Fragment key={data.id}>
                <Activity {...data} sx={{ mb: 3 }} />
                <Divider sx={{ mb: 3 }} />
              </React.Fragment>
            ))} */}
                </Stack>
              </PanelTabContent>
            </Box>
          </>
        )}
      </Panel>
      <OrderItemPanel open={openPanelOrder} onClose={hidePanelOrderItem} itemId={selectedOrderItemId} />
    </>
  );
};

function a11yProps(index: number) {
  return {
    id: `order-tab-${index}`,
    "aria-controls": `order-tabpanel-${index}`,
  };
}

export default OrderPanel;
